import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: EmblaCarouselStyles;
    children?: React.ReactNode;
    valuePass?: any;
}

type Props = OwnProps;

export interface EmblaCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
}

export const EmblaCarouselStyles: EmblaCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        align: "middle",
        color: "primary",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "primary",
        color: "primary",
        css: css`
            border: 0;
            padding: 0;
            margin: 0 19px;
            outline: 0;
            z-index: 9;
            min-height: 28px;
            min-width: 28px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin: 0 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 auto;
            padding: 0;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        color: "primary",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "primary",
        color: "primary",
        css: css`
            border: 0;
            padding: 0;
            margin: 0 19px;
            outline: 0;
            z-index: 9;
            min-height: 28px;
            min-width: 28px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin: 0 10px;
                    `,
                    null,
                    null,
                ])}
            > ${IconWrapper} {
                top: 2px;
            }
        `,
    },
};

const EmblaCarousel = ({ extendedStyles, children, valuePass }: Props) => {
    const [styles] = useState(() => mergeToNew(EmblaCarouselStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [, setCanScrollPrev] = useState(false);
    const [, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            loop: true,
        });
        setCanScroll();
    }, [embla]);
    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);
    useRecursiveTimeout(scrollNext, valuePass * 1000);
    return (
        <GridContainer {...styles.mainContainer}>
            <GridItem {...styles.prevArrowGridItem}>
                <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                    <ButtonIcon src={ChevronLeft} />
                </Button>
            </GridItem>
            <GridItem {...styles.carouselGridItem}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.carouselSlidesContainer}>{children}</StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.nextArrowGridItem}>
                <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                    <ButtonIcon src={ChevronRight} />
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default EmblaCarousel;
